'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useSocketContext } from '@/contexts/SocketContext';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { STORAGE_KEY } from '@/constants/stakeholders';
import { useQueryClient } from '@tanstack/react-query';
import {
	STAKEHOLDERS_V2_INSUFFICIENT_DATA_EVENT,
	StakeholdersReportEvent,
	isInsufficientDataReportEvent,
} from './stakeholders-report-events';

interface ReportProgress {
	companyId: number;
	companyName: string;
	stage?: string;
	progress?: number;
	departmentId?: number;
	departmentName?: string;
}

export const useStakeholdersNotifications = () => {
	const socket = useSocketContext();
	const router = useRouter();
	const queryClient = useQueryClient();
	const reportProgressRef = useRef<Map<string, ReportProgress>>(new Map());

	// Save active reports to localStorage
	const saveActiveReports = useCallback(() => {
		try {
			const reports = Array.from(reportProgressRef.current.values());
			if (reports.length > 0) {
				localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
			} else {
				localStorage.removeItem(STORAGE_KEY);
			}
		} catch (error) {
			console.error('Failed to save active reports:', error);
		}
	}, []);

	// Restore active reports from localStorage
	const restoreActiveReports = useCallback(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				const reports = JSON.parse(saved) as ReportProgress[];
				reports.forEach((report) => {
					const reportKey = report.departmentId
						? `${report.companyId}-${report.departmentId}`
						: `${report.companyId}-v2`;

					reportProgressRef.current.set(reportKey, report);
				});
			}
		} catch (error) {
			console.error('Failed to restore active reports:', error);
			localStorage.removeItem(STORAGE_KEY);
		}
	}, []);

	useEffect(() => {
		// Restore active reports on mount
		restoreActiveReports();

		const getReportKey = (companyId: number | string, departmentId?: number) => {
			return departmentId ? `${companyId}-${departmentId}` : `${companyId}-v2`;
		};

		// Handler for successful completion
		const handleSuccess = (data: StakeholdersReportEvent) => {
			const reportKey = getReportKey(data.companyId, data.departmentId);
			const progressInfo = reportProgressRef.current.get(reportKey);

			const companyName = data.companyName || progressInfo?.companyName || `Company ${data.companyId}`;

			queryClient.invalidateQueries({ queryKey: ['stakeholdersReports', data.companyId] });

			toast.success('Stakeholders generated', {
				description: (
					<span className='text-xs text-[#78716C]'>
						Stakeholders for {companyName} are ready{' '}
						<button
							onClick={() => {
								queryClient.invalidateQueries({ queryKey: ['stakeholdersReports', data.companyId] });
								router.push(`/company/${data.companyId}?tab=stakeholders`);
							}}
							className="underline font-medium text-[#5049E5] cursor-pointer"
							style={{ background: 'none', border: 'none', padding: 0, color: 'inherit' }}
						>
							See results
						</button>
					</span>
				),
				style: {
					backgroundColor: '#DBFBE6', color: '#117E30', fontWeight: 600, fontSize: 12, border: '1px solid #85E0A5'
				}
			});

			// Cleanup
			reportProgressRef.current.delete(reportKey);
			saveActiveReports();
		};

		// Handler for failures
		const handleInsufficientData = (data: StakeholdersReportEvent) => {
			const reportKey = getReportKey(data.companyId, data.departmentId);
			reportProgressRef.current.delete(reportKey);
			saveActiveReports();
		};

		const handleFailed = (data: StakeholdersReportEvent) => {
			if (isInsufficientDataReportEvent(data)) {
				handleInsufficientData(data);
				return;
			}

			const reportKey = getReportKey(data.companyId, data.departmentId);
			const progressInfo = reportProgressRef.current.get(reportKey);

			const companyName = data.companyName || progressInfo?.companyName || `Company ${data.companyId}`;
			const departmentInfo = data.departmentName || progressInfo?.departmentName
				? ` (${data.departmentName || progressInfo?.departmentName})`
				: '';
			const errorMessage = data.error || data.reason || 'An error occurred during generation';

			toast.error('Report generation failed', {
				description: `Failed to generate report for ${companyName}${departmentInfo}. ${errorMessage}`,
				action: {
					label: 'Try Again',
					onClick: () => router.push(`/company/${data.companyId}?tab=stakeholders`),
				},
			});

			// Cleanup
			reportProgressRef.current.delete(reportKey);
			saveActiveReports();
		};

		// Subscribe to all stakeholders report events
		const unsubSuccess = socket.on('stakeholders_report_v2_success', handleSuccess);
		const unsubFailed = socket.on('stakeholders_report_v2_failed', handleFailed);
		const unsubInsufficientData = socket.on(STAKEHOLDERS_V2_INSUFFICIENT_DATA_EVENT, handleInsufficientData);

		// Cleanup on unmount
		return () => {
			unsubSuccess();
			unsubFailed();
			unsubInsufficientData();

			reportProgressRef.current.clear();
		};
	}, [socket, router, queryClient, restoreActiveReports, saveActiveReports]);
};
